import React, { useEffect, useState } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  TextField,
  Grid,
  Paper,
  MenuItem,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { createPigmyCustomerService } from "../../../services/customerService";
import { imageUploadService } from "../../../services/imageUploadService";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getAllAgentsService } from "../../../services/agentService";

const steps = ["Customer Information", "Guarantor Information"];

const validationSchema = Yup.object({
  userName: Yup.string(),
  phoneNumber: Yup.string(),
  userPhoto: Yup.string().url("Invalid URL"),
  alternativePhoneNumber1: Yup.string(),
  alternativePhoneNumber2: Yup.string(),
  password: Yup.string(),
  email: Yup.string().email("Invalid email"),
  address: Yup.string(),
  landmark: Yup.string(),
  locationLink: Yup.string(),
  customerPhoto: Yup.string().url("Invalid URL"),
  passBookImage: Yup.string().url("Invalid URL"),
  signatureImage: Yup.string().url("Invalid URL"),
  agent: Yup.string().required("Agent is required"),
  pigmyPlan: Yup.string().required("Pigmy Plan is required"),
  frequency: Yup.string().required("Frequency is required"),
  collectionAmount: Yup.number()
    .required("Collection Amount is required")
    .positive("Amount must be positive"),
  startDate: Yup.date().required("Start Date is required"),
  endDate: Yup.date().required("End Date is required"),
  nomineeName: Yup.string().required("Nominee Name is required"),
  nomineeRelation: Yup.string().required("Nominee Relation is required"),
  nomineePhoneNumber: Yup.string().required("Nominee Phone Number is required"),
  nomineeAadhaarNumber: Yup.string().required(
    "Nominee Aadhaar Number is required"
  ),
  nomineePanNumber: Yup.string().required("Nominee Pan Number is required"),
  nomineeAccountNumber: Yup.string().required(
    "Nominee Account Number is required"
  ),
  panNumber: Yup.string().required("Nominee Pan Number is required"),
  aadhaarNumber: Yup.string().required(" Aadhaar Number is required"),
  nomineeAccountName: Yup.string().required("Nominee Account Name is required"),
  nomineeIFSC: Yup.string().required("Nominee IFSC is required"),
  nomineeBranch: Yup.string().required("Nominee Branch is required"),
  accountNumber: Yup.string().required("Account Number is required"),
  accountName: Yup.string().required(" Account Name is required"),
  IFSC: Yup.string().required(" IFSC is required"),
  branch: Yup.string().required(" Branch is required"),
});

const CreatePigmyCustomer = () => {
  const [agents, setAgents] = React.useState();
  const [activeStep, setActiveStep] = React.useState(0);
  const navigate = useNavigate();
  const [isSubmit, setIsSubmit] = useState(false);

  const fetchAgents = async () => {
    try {
      const agents = await getAllAgentsService(0, 50);
      setAgents(agents?.agentsWithPhoneContacts); // Adjust this based on API response structure
    } catch (err) {
      toast.error(err.message);
    }
  };

  const calculateDates = (plan) => {
    const startDate = new Date();
    let endDate = new Date();
    if (plan === "12") {
      endDate.setFullYear(startDate.getFullYear() + 1);
    } else if (plan === "6") {
      endDate.setMonth(startDate.getMonth() + 6);
    } else if (plan === "24") {
      endDate.setFullYear(startDate.getFullYear() + 2);
    } else if (plan === "36") {
      endDate.setFullYear(startDate.getFullYear() + 3);
    }
    return {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };
  };

  const PhotoField = ({ name, label, handleFileUpload }) => (
    <Grid item xs={3}>
      <Typography mt={1}>{label}</Typography>
      <Field name={name}>
        {({ field, form }) => (
          <TextField
            fullWidth
            size="small"
            type="file"
            sx={{
              "& .MuiInputBase-root": {
                fontSize: "0.875rem", // Smaller font size
                borderRadius: "8px", // Adjust this value for border radius
              },
            }}
            onChange={(event) => {
              const file = event.currentTarget.files[0];
              handleFileUpload(file, form.setFieldValue, field.name);
            }}
          />
        )}
      </Field>
    </Grid>
  );

  const handleFileUpload = async (file, setFieldValue, fieldName) => {
      if (!file) {
        toast.error("Please select a file to upload");
        return;
      }
  
      const allowedExtensions = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "image/webp",
      ];
  
      if (!allowedExtensions.includes(file.type)) {
        toast.error("Only image files (jpg, jpeg, png, webp) are allowed.");
        return;
      }
  
      const formData = new FormData();
      formData.append("image", file);
  
      try {
        const imageUrl = await imageUploadService(formData);
        setFieldValue(fieldName, imageUrl);
      } catch (error) {
        toast.error("Image upload failed. Please try again.");
        console.error(error);
      }
    };
    

  const isLastStep = activeStep === steps.length - 1;

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleSubmit = async (values, actions) => {
    try {
      console.log(values);
      setIsSubmit(true);
      const createCustomer = await createPigmyCustomerService(values);
      if (createCustomer.status === "success") {
        toast.success("Customer created successfully");
        // Navigate to the dashboard
        navigate("/customer");
        setIsSubmit(false);
      }

      actions.setSubmitting(false);
    } catch (error) {
      toast.error(error);
    }
  };
  const handlePanChange = (e, setFieldValue, isCus) => {
    let value = e.target.value.toUpperCase(); // Convert to uppercase
    value = value.replace(/[^A-Z0-9]/g, ""); // Remove non-alphanumeric characters

    // Enforce correct structure: 5 letters, 4 numbers, 1 letter
    if (value.length > 10) {
      value = value.slice(0, 10);
    }

    if (value.length <= 5) {
      value = value.replace(/[^A-Z]/g, ""); // First 5 should be letters
    } else if (value.length <= 9) {
      value = value.replace(/^(.{5})[^0-9]*/, "$1"); // Next 4 should be numbers
    } else {
      value = value.replace(/^(.{5}\d{4})[^A-Z]*/, "$1"); // Last character should be a letter
    }
    if (isCus) {
      setFieldValue("panNumber", value); // Update Formik state
    } else {
      setFieldValue("nomineePanNumber", value); // Update Formik state
    }
  };
  const handleStartDate = (values, date) => {
    let startDate = new Date(date);
    let endDate = new Date(startDate); // Copy startDate
    let plan = values.pigmyPlan;

    if (plan === "12") {
      endDate.setFullYear(startDate.getFullYear() + 1);
    } else if (plan === "6") {
      endDate.setMonth(startDate.getMonth() + 6);
    } else if (plan === "24") {
      endDate.setFullYear(startDate.getFullYear() + 2);
    } else if (plan === "36") {
      endDate.setFullYear(startDate.getFullYear() + 3);
    }

    return {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };
  };

  const renderStepContent = (values, setFieldValue, handleChange) => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <Typography mt={1}>Customer Name</Typography>
          <Field
            fullWidth
            size="small"
            name="userName"
            as={TextField}
            required
            onKeyDown={(e) => {
              // Allow only alphabets and spaces
              if (
                !/[a-zA-Z\s]/.test(e.key) && // Allow alphabets and spaces
                e.key !== "Backspace" && // Allow Backspace
                e.key !== "Delete" && // Allow Delete
                e.key !== "ArrowLeft" && // Allow Left Arrow
                e.key !== "ArrowRight" // Allow Right Arrow
              ) {
                e.preventDefault();
              }
            }}
            sx={{
              "& .MuiInputBase-root": {
                // Smaller font size
                borderRadius: "8px", // Adjust this value for border radius
              },
            }}
            inputProps={{
              onInput: (e) => {
                e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, ""); // Allow only alphabets and spaces
              },
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <Typography mt={1}>Phone Number</Typography>
          <Field
            fullWidth
            size="small"
            name="phoneNumber"
            required
            type="text"
            as={TextField}
            inputProps={{
              maxLength: 10, // Limits the input to 10 characters
              inputMode: "numeric", // Brings up a numeric keyboard on mobile devices
              style: { appearance: "textfield" }, // Remove arrows for most browsers
            }}
            sx={{
              "& input": {
                MozAppearance: "textfield", // Remove arrows for Firefox
              },
            }}
            onInput={(e) => {
              e.target.value = e.target.value
                .replace(/[^0-9]/g, "")
                .slice(0, 10); // Allow only numbers and limit to 10 digits
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <Typography mt={1}>Alternative Phone Number</Typography>
          <Field
            fullWidth
            size="small"
            required
            name="alternativePhoneNumber1"
            as={TextField}
            inputProps={{
              maxLength: 10, // Limits the input to 10 characters
              inputMode: "numeric", // Brings up a numeric keyboard on mobile devices
              style: { appearance: "textfield" }, // Remove arrows for most browsers
            }}
            sx={{
              "& input": {
                MozAppearance: "textfield", // Remove arrows for Firefox
              },
            }}
            onInput={(e) => {
              e.target.value = e.target.value
                .replace(/[^0-9]/g, "")
                .slice(0, 10); // Allow only numbers and limit to 10 digits
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <Typography mt={1}>Alternative Phone Number 2</Typography>
          <Field
            fullWidth
            size="small"
            required
            name="alternativePhoneNumber2"
            as={TextField}
            inputProps={{
              maxLength: 10, // Limits the input to 10 characters
              inputMode: "numeric", // Brings up a numeric keyboard on mobile devices
              style: { appearance: "textfield" }, // Remove arrows for most browsers
            }}
            sx={{
              "& input": {
                MozAppearance: "textfield", // Remove arrows for Firefox
              },
            }}
            onInput={(e) => {
              e.target.value = e.target.value
                .replace(/[^0-9]/g, "")
                .slice(0, 10); // Allow only numbers and limit to 10 digits
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <Typography mt={1}>Password</Typography>
          <Field
            fullWidth
            size="small"
            name="password"
            as={TextField}
            sx={{
              "& .MuiInputBase-root": {
                // Smaller font size
                borderRadius: "8px", // Adjust this value for border radius
              },
            }}
            type="password"
          />
        </Grid>
        <Grid item xs={3}>
          <Typography mt={1}>Email</Typography>
          <Field
            fullWidth
            size="small"
            name="email"
            as={TextField}
            sx={{
              "& .MuiInputBase-root": {
                // Smaller font size
                borderRadius: "8px", // Adjust this value for border radius
              },
            }}
          />
        </Grid>
        <PhotoField
          name="userPhoto"
          label="Customer Photo"
          handleFileUpload={handleFileUpload}
        />
        <Grid item xs={3}>
          <Typography mt={1}>Address</Typography>
          <Field
            fullWidth
            size="small"
            name="address"
            required
            as={TextField}
            sx={{
              "& .MuiInputBase-root": {
                // Smaller font size
                borderRadius: "8px", // Adjust this value for border radius
              },
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <Typography mt={1}>Aadhaar Number</Typography>
          <Field
            fullWidth
            size="small"
            name="aadhaarNumber"
            required
            as={TextField}
            onKeyDown={(e) => {
              // Allow only numbers
              if (
                !/[0-9]/.test(e.key) && // Allow digits
                e.key !== "Backspace" && // Allow Backspace
                e.key !== "Delete" && // Allow Delete
                e.key !== "ArrowLeft" && // Allow Left Arrow
                e.key !== "ArrowRight" // Allow Right Arrow
              ) {
                e.preventDefault();
              }
            }}
            inputProps={{
              maxLength: 12, // Restrict length to 10
            }}
            sx={{
              "& .MuiInputBase-root": {
                // Smaller font size
                borderRadius: "8px", // Adjust this value for border radius
              },
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <Typography mt={1}>PAN Number</Typography>
          <Field
            fullWidth
            size="small"
            name="panNumber"
            required
            as={TextField}
            inputProps={{
              maxLength: 10, // Restrict length to 10
            }}
            sx={{
              "& .MuiInputBase-root": {
                borderRadius: "8px", // Adjust this value for border radius
              },
            }}
            onChange={(e) => handlePanChange(e, setFieldValue, true)}
          />
        </Grid>
        <PhotoField
          name="passBookImage"
          label="Pass Book Image"
          handleFileUpload={handleFileUpload}
        />{" "}
        <PhotoField
          name="signatureImage"
          label="Signature Image"
          handleFileUpload={handleFileUpload}
        />
        <Grid item xs={3}>
          <Typography mt={1}> Account Number</Typography>
          <Field
            fullWidth
            size="small"
            name="accountNumber"
            as={TextField}
            onKeyDown={(e) => {
              // Allow only numbers
              if (
                !/[0-9]/.test(e.key) && // Allow digits
                e.key !== "Backspace" && // Allow Backspace
                e.key !== "Delete" && // Allow Delete
                e.key !== "ArrowLeft" && // Allow Left Arrow
                e.key !== "ArrowRight" // Allow Right Arrow
              ) {
                e.preventDefault();
              }
            }}
            inputProps={{
              maxLength: 18, // Restrict length to 10
            }}
            sx={{
              "& .MuiInputBase-root": {
                // Smaller font size
                borderRadius: "8px", // Adjust this value for border radius
              },
            }}
          />
        </Grid>
        <Grid item xs={3}>
          {" "}
          <Typography mt={1}>Bank Name</Typography>
          <Field
            fullWidth
            size="small"
            name="bankName"
            as={TextField}
            onKeyDown={(e) => {
              // Allow only alphabets and spaces
              if (
                !/[a-zA-Z\s]/.test(e.key) && // Allow alphabets and spaces
                e.key !== "Backspace" && // Allow Backspace
                e.key !== "Delete" && // Allow Delete
                e.key !== "ArrowLeft" && // Allow Left Arrow
                e.key !== "ArrowRight" // Allow Right Arrow
              ) {
                e.preventDefault();
              }
            }}
            sx={{
              "& .MuiInputBase-root": {
                // Smaller font size
                borderRadius: "8px", // Adjust this value for border radius
              },
            }}
            inputProps={{
              onInput: (e) => {
                e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, ""); // Allow only alphabets and spaces
              },
            }}
          />
        </Grid>
        <Grid item xs={3}>
          {" "}
          <Typography mt={1}>Account Name</Typography>
          <Field
            fullWidth
            size="small"
            name="accountName"
            required
            as={TextField}
            inputProps={{
              maxLength: 20, // Limits the input to 10 characters
              inputMode: "numeric", // Brings up a numeric keyboard on mobile devices
              style: { appearance: "textfield" }, // Remove arrows for most browsers
            }}
            sx={{
              "& input": {
                MozAppearance: "textfield", // Remove arrows for Firefox
              },
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <Typography mt={1}>IFSC</Typography>
          <Field
            fullWidth
            size="small"
            name="IFSC"
            required
            as={TextField}
            sx={{
              "& .MuiInputBase-root": {
                borderRadius: "8px",
              },
            }}
          />
        </Grid>
        <Grid item xs={3}>
          {" "}
          <Typography mt={1}> Branch</Typography>
          <Field
            fullWidth
            size="small"
            name="branch"
            required
            as={TextField}
            onKeyDown={(e) => {
              // Allow only alphabets and spaces
              if (
                !/[a-zA-Z\s]/.test(e.key) && // Allow alphabets and spaces
                e.key !== "Backspace" && // Allow Backspace
                e.key !== "Delete" && // Allow Delete
                e.key !== "ArrowLeft" && // Allow Left Arrow
                e.key !== "ArrowRight" // Allow Right Arrow
              ) {
                e.preventDefault();
              }
            }}
            sx={{
              "& .MuiInputBase-root": {
                // Smaller font size
                borderRadius: "8px", // Adjust this value for border radius
              },
            }}
            inputProps={{
              onInput: (e) => {
                e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, ""); // Allow only alphabets and spaces
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography>Pigmy Details</Typography>
        </Grid>
        <Grid item xs={3}>
          <Typography mt={1}>Agent</Typography>
          <Field
            as={TextField}
            size="small"
            name="agent"
            select
            fullWidth
            required
          >
            {agents?.map((agent) => (
              <MenuItem key={agent._id} value={agent._id}>
                {agent?.userName}
              </MenuItem>
            ))}
          </Field>
        </Grid>
        <Grid item xs={3}>
          <Typography mt={1}>Pigmy Plan *</Typography>
          <Field
            as={TextField}
            name="pigmyPlan"
            size="small"
            select
            fullWidth
            required
            onChange={(e) => {
              handleChange(e);
              const { startDate, endDate } = calculateDates(e.target.value);
              setFieldValue("startDate", startDate);
              setFieldValue("endDate", endDate);
            }}
          >
            <MenuItem value="6">6 months</MenuItem>
            <MenuItem value="12">12 months</MenuItem>
            <MenuItem value="24">24 months</MenuItem>
            <MenuItem value="36">36 months</MenuItem>
          </Field>
        </Grid>
        <Grid item xs={3}>
          <Typography>Frequency *</Typography>
          <Field
            as={TextField}
            name="frequency"
            size="small"
            select
            fullWidth
            required
          >
            <MenuItem value="Daily">Daily</MenuItem>
            <MenuItem value="Weekly">Weekly</MenuItem>
            <MenuItem value="Monthly">Monthly</MenuItem>
          </Field>
        </Grid>
        <Grid item xs={3}>
          <Typography>Collection Amount *</Typography>
          <Field
            as={TextField}
            size="small"
            name="collectionAmount"
            fullWidth
            required
            onKeyDown={(e) => {
              // Allow only numbers
              if (
                !/[0-9]/.test(e.key) && // Allow digits
                e.key !== "Backspace" && // Allow Backspace
                e.key !== "Delete" && // Allow Delete
                e.key !== "ArrowLeft" && // Allow Left Arrow
                e.key !== "ArrowRight" // Allow Right Arrow
              ) {
                e.preventDefault();
              }
            }}
            inputProps={{
              maxLength: 12, // Restrict length to 10
            }}
            sx={{
              "& .MuiInputBase-root": {
                // Smaller font size
                borderRadius: "8px", // Adjust this value for border radius
              },
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <Typography>Start Date *</Typography>
          <Field
            as={TextField}
            name="startDate"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            fullWidth
            onChange={(e) => {
              handleChange(e);
              const { startDate, endDate } = handleStartDate(
                values,
                e.target.value
              );
              setFieldValue("startDate", startDate);
              setFieldValue("endDate", endDate);
            }}
            required
          />
        </Grid>
        <Grid item xs={3}>
          <Typography>End Date *</Typography>
          <Field
            as={TextField}
            name="endDate"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={3}>
          <Typography mt={1}>Nominee Name</Typography>
          <Field
            fullWidth
            required
            size="small"
            name="nomineeName"
            as={TextField}
            onKeyDown={(e) => {
              // Allow only alphabets and spaces
              if (
                !/[a-zA-Z\s]/.test(e.key) && // Allow alphabets and spaces
                e.key !== "Backspace" && // Allow Backspace
                e.key !== "Delete" && // Allow Delete
                e.key !== "ArrowLeft" && // Allow Left Arrow
                e.key !== "ArrowRight" // Allow Right Arrow
              ) {
                e.preventDefault();
              }
            }}
            sx={{
              "& .MuiInputBase-root": {
                // Smaller font size
                borderRadius: "8px", // Adjust this value for border radius
              },
            }}
            inputProps={{
              onInput: (e) => {
                e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, ""); // Allow only alphabets and spaces
              },
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <Typography mt={1}>Nominee Relation</Typography>
          <Field
            fullWidth
            required
            size="small"
            name="nomineeRelation"
            as={TextField}
            onKeyDown={(e) => {
              // Allow only alphabets and spaces
              if (
                !/[a-zA-Z\s]/.test(e.key) && // Allow alphabets and spaces
                e.key !== "Backspace" && // Allow Backspace
                e.key !== "Delete" && // Allow Delete
                e.key !== "ArrowLeft" && // Allow Left Arrow
                e.key !== "ArrowRight" // Allow Right Arrow
              ) {
                e.preventDefault();
              }
            }}
            sx={{
              "& .MuiInputBase-root": {
                // Smaller font size
                borderRadius: "8px", // Adjust this value for border radius
              },
            }}
            inputProps={{
              onInput: (e) => {
                e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, ""); // Allow only alphabets and spaces
              },
            }}
          />
        </Grid>
        <Grid item xs={3}>
          {" "}
          <Typography mt={1}>Nominee Phone Number</Typography>
          <Field
            fullWidth
            required
            size="small"
            name="nomineePhoneNumber"
            as={TextField}
            inputProps={{
              maxLength: 10, // Limits the input to 10 characters
              inputMode: "numeric", // Brings up a numeric keyboard on mobile devices
              style: { appearance: "textfield" }, // Remove arrows for most browsers
            }}
            sx={{
              "& input": {
                MozAppearance: "textfield", // Remove arrows for Firefox
              },
            }}
            onInput={(e) => {
              e.target.value = e.target.value
                .replace(/[^0-9]/g, "")
                .slice(0, 10); // Allow only numbers and limit to 10 digits
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <Typography mt={1}>Nominee Aadhaar Number</Typography>
          <Field
            fullWidth
            required
            size="small"
            name="nomineeAadhaarNumber"
            as={TextField}
            onKeyDown={(e) => {
              // Allow only numbers
              if (
                !/[0-9]/.test(e.key) && // Allow digits
                e.key !== "Backspace" && // Allow Backspace
                e.key !== "Delete" && // Allow Delete
                e.key !== "ArrowLeft" && // Allow Left Arrow
                e.key !== "ArrowRight" // Allow Right Arrow
              ) {
                e.preventDefault();
              }
            }}
            inputProps={{
              maxLength: 12, // Restrict length to 10
            }}
            sx={{
              "& .MuiInputBase-root": {
                // Smaller font size
                borderRadius: "8px", // Adjust this value for border radius
              },
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <Typography mt={1}>Nominee Pan Number</Typography>
          <Field
            fullWidth
            required
            size="small"
            name="nomineePanNumber"
            as={TextField}
            sx={{
              "& .MuiInputBase-root": {
                borderRadius: "8px",
              },
            }}
            onChange={(e) => handlePanChange(e, setFieldValue, false)}
          />
        </Grid>
        <Grid item xs={3}>
          <Typography mt={1}>Nominee Account Number</Typography>
          <Field
            fullWidth
            required
            size="small"
            name="nomineeAccountNumber"
            as={TextField}
            onKeyDown={(e) => {
              // Allow only numbers
              if (
                !/[0-9]/.test(e.key) && // Allow digits
                e.key !== "Backspace" && // Allow Backspace
                e.key !== "Delete" && // Allow Delete
                e.key !== "ArrowLeft" && // Allow Left Arrow
                e.key !== "ArrowRight" // Allow Right Arrow
              ) {
                e.preventDefault();
              }
            }}
            inputProps={{
              maxLength: 18, // Restrict length to 10
            }}
            sx={{
              "& .MuiInputBase-root": {
                // Smaller font size
                borderRadius: "8px", // Adjust this value for border radius
              },
            }}
          />
        </Grid>
        <Grid item xs={3}>
          {" "}
          <Typography mt={1}>Nominee Account Name</Typography>
          <Field
            fullWidth
            required
            size="small"
            name="nomineeAccountName"
            as={TextField}
            onKeyDown={(e) => {
              // Allow only alphabets and spaces
              if (
                !/[a-zA-Z\s]/.test(e.key) && // Allow alphabets and spaces
                e.key !== "Backspace" && // Allow Backspace
                e.key !== "Delete" && // Allow Delete
                e.key !== "ArrowLeft" && // Allow Left Arrow
                e.key !== "ArrowRight" // Allow Right Arrow
              ) {
                e.preventDefault();
              }
            }}
            sx={{
              "& .MuiInputBase-root": {
                // Smaller font size
                borderRadius: "8px", // Adjust this value for border radius
              },
            }}
            inputProps={{
              onInput: (e) => {
                e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, ""); // Allow only alphabets and spaces
              },
            }}
          />
        </Grid>
        <Grid item xs={3}>
          <Typography mt={1}>Nominee IFSC</Typography>
          <Field
            fullWidth
            required
            size="small"
            name="nomineeIFSC"
            as={TextField}
            sx={{
              "& .MuiInputBase-root": {
                borderRadius: "8px",
              },
            }}
          />
        </Grid>
        <Grid item xs={3}>
          {" "}
          <Typography mt={1}>Nominee Branch</Typography>
          <Field
            fullWidth
            required
            size="small"
            name="nomineeBranch"
            as={TextField}
            onKeyDown={(e) => {
              // Allow only alphabets and spaces
              if (
                !/[a-zA-Z\s]/.test(e.key) && // Allow alphabets and spaces
                e.key !== "Backspace" && // Allow Backspace
                e.key !== "Delete" && // Allow Delete
                e.key !== "ArrowLeft" && // Allow Left Arrow
                e.key !== "ArrowRight" // Allow Right Arrow
              ) {
                e.preventDefault();
              }
            }}
            sx={{
              "& .MuiInputBase-root": {
                // Smaller font size
                borderRadius: "8px", // Adjust this value for border radius
              },
            }}
            inputProps={{
              onInput: (e) => {
                e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, ""); // Allow only alphabets and spaces
              },
            }}
          />
        </Grid>
      </Grid>
    );
  };

  useEffect(() => {
    fetchAgents();
  }, []);

  return (
    <Paper sx={{ background: "#fff" }}>
      <Box sx={{ width: "100%", p: 3 }}>
        <Formik
          initialValues={{
            userName: "",
            phoneNumber: "",
            userPhoto: "",
            alternativePhoneNumber1: "",
            alternativePhoneNumber2: "",
            password: "",
            email: "",
            address: "",
            agent: "",
            pigmyPlan: "",
            frequency: "",
            collectionAmount: "",
            startDate: "",
            endDate: "",
            nomineeName: "",
            nomineeRelation: "",
            nomineePhoneNumber: "",
            nomineeAadhaarNumber: "",
            nomineePanNumber: "",
            nomineeAccountNumber: "",
            nomineeAccountName: "",
            nomineeIFSC: "",
            nomineeBranch: "",
            accountNumber: "",
            accountName: "",
            IFSC: "",
            branch: "",
            passBookImage: "",
            signatureImage: "",
            aadhaarNumber: "",
            panNumber: "",
            bankName: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, setFieldValue, handleChange, values }) => (
            <Box sx={{ px: 2 }}>
              <Form>
                {renderStepContent(values, setFieldValue, handleChange)}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                  }}
                >
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={isSubmit}
                    sx={{ borderRadius: "8px" }}
                  >
                    Submit
                  </Button>
                </Box>
              </Form>
            </Box>
          )}
        </Formik>
      </Box>
    </Paper>
  );
};

export default CreatePigmyCustomer;
